import TrustedHtml from '@components/common/TrustedHtml/TrustedHtml'
import ArrowRightIcon from '@components/icons/ArrowRightIcon/ArrowRightIcon'
import {
  NewsletterCountrySelectorField,
  NewsletterDropdownField,
  NewsletterField,
  NewsletterInputField,
  NewsletterTextBlock,
} from '@components/modules/NewsletterSignup/NewsletterSignup'

import Button from '@components/ui/ButtonV2/Button'
import Checkbox from '@components/ui/Checkbox/Checkbox'
import LineClamp from '@components/ui/LineClamp/LineClamp'
import Select from '@components/ui/Select/Select'
import { atLeastTwo } from '@lib/boolean-functions'
import ClickOutside from '@lib/click-outside'
import { selectCountry } from '@redux/globalSlice'
import { isMobile } from '@utils/helpers/DeviceUtils'
import { pushNewsletterTrackingEvent } from '@utils/tracking-utils'
import cn from 'clsx'
import { validate } from 'email-validator'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'

import { useCountries } from '@features/countries/api/get-countries'
import { SendGridMailingList, SendGridRequest } from 'pages/api/email-sign-up'
import React, { FC, FormEventHandler, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styles from './Signup.module.scss'

type SignupProps = {
  signupText: string
  emailPlaceholderLabel: string
  enableConsumerSignup: boolean
  enableProfessionalSignup: boolean
  enablePartnerSignup: boolean
  enablePressSignup: boolean
  consumerCheckboxLabel: string
  professionalCheckboxLabel: string
  partnerCheckboxLabel: string
  pressCheckboxLabel: string
  acceptTerms: string
  acceptTermsReadMore: string
  enableAcceptTermsCheckbox: boolean
  signupButtonLabel: string
  newsletterConsentMessage: string
  signUpForNewsletter: boolean
  signupCompletedMessage: string
  source?: string
  videoScroll?: boolean
  expand?: boolean
  clickOutside?: boolean
  fields: NewsletterField[]
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>

const setDefaultList = (
  consumer: boolean,
  professional: boolean,
  partner: boolean,
  press: boolean,
): SendGridMailingList => {
  if (consumer) return 'CONSUMERS'
  if (professional) return 'PROFESSIONALS'
  if (partner) return 'PARTNERS'
  if (press) return 'PRESS'
  return 'CONSUMERS'
}

//sign up
const Signup: FC<SignupProps> = ({
  signupText,
  emailPlaceholderLabel,
  enableConsumerSignup,
  enableProfessionalSignup,
  enablePartnerSignup,
  signUpForNewsletter,
  newsletterConsentMessage,
  enablePressSignup,
  consumerCheckboxLabel,
  professionalCheckboxLabel,
  partnerCheckboxLabel,
  pressCheckboxLabel,
  acceptTerms,
  acceptTermsReadMore,
  enableAcceptTermsCheckbox,
  signupButtonLabel,
  signupCompletedMessage,
  source = 'web page gubi',
  videoScroll,
  expand,
  clickOutside = false,
  ...args
}) => {
  const router = useRouter()
  const [fields, setFields] = useState<NewsletterField[] | null>(null)
  const [checked, setChecked] = useState<{
    list: SendGridMailingList
    terms: boolean
    newsletterConsent: boolean
  }>({
    list: setDefaultList(
      enableConsumerSignup,
      enableProfessionalSignup,
      enablePartnerSignup,
      enablePressSignup,
    ),
    terms: enableAcceptTermsCheckbox ? false : true,
    newsletterConsent: false,
  })

  const country = useSelector(selectCountry)
  const [completed, setCompleted] = useState<boolean>(false)
  const [displaySubmit, setDisplaySubmit] = useState<boolean>(false)

  useEffect(() => {
    if (expand) setDisplaySubmit(expand)
  }, [expand])
  const emailInput = useRef<HTMLInputElement>(null)
  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    let validEmail = true
    e.preventDefault()
    //validate email;

    const formData = new FormData(e.currentTarget)
    const customFields = [...formData.entries()]

    if (emailInput.current) {
      validEmail = validate(emailInput.current.value)
      if (!validEmail) {
        emailInput.current.setCustomValidity('Invalid Input')
        emailInput.current.reportValidity()
      } else {
        const urls = window.dataLayer.filter((x: any) => x.event == 'pageview')
        const lastUrl = urls.length > 1 ? urls[urls.length - 2].pagePath : ''
        const url = urls[urls.length - 1].pagePath
        const requestObj: SendGridRequest = {
          list: checked.list,
          email: emailInput.current.value,
          language: router.query.language as string,
          market: country?.code,
          geoCountry: Cookies.get('homeCountry') ?? '',
          url: url,
          signupDate: new Date().toISOString(),
          urlLastClick: lastUrl,
          source: source,
          ...customFields
            .map((x) => ({ [x[0]]: x[1] }))
            .reduce((a, b) => ({ ...a, ...b }), {}),
          newsletterSignup: signUpForNewsletter
            ? checked.newsletterConsent
            : true,
        }

        const req = await fetch('/api/email-sign-up', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestObj),
        })

        if (
          (signUpForNewsletter && checked.newsletterConsent) ||
          !signUpForNewsletter
        ) {
          pushNewsletterTrackingEvent({
            newsletter: checked.list,
            email: emailInput.current.value,
          })
        }

        setCompleted(req.ok)
      }
    }
  }

  useEffect(() => {
    if (args.fields) setFields(args.fields)
  }, [args.fields])

  return enableConsumerSignup ||
    enableProfessionalSignup ||
    enablePartnerSignup ||
    enablePressSignup ? (
    <ClickOutside active={clickOutside} onClick={() => setDisplaySubmit(false)}>
      <div className={styles.root} {...args}>
        {completed ? (
          <TrustedHtml
            html={
              !!signupCompletedMessage
                ? signupCompletedMessage
                : 'Sign up completed'
            }
          />
        ) : (
          <>
            <TrustedHtml
              className={cn(
                videoScroll && {
                  'line-clamp-1 lg:line-clamp-none': !displaySubmit,
                },
              )}
              html={!!signupText ? signupText : 'Sign up text'}
            />
            {videoScroll && (
              <div
                className="absolute right-0 px-8 lg:hidden"
                onClick={() => setDisplaySubmit(!displaySubmit)}
              >
                <ArrowRightIcon
                  color={'black'}
                  width={'8'}
                  height={'20'}
                  className={cn(
                    styles.expandIcon,
                    displaySubmit && styles.open,
                  )}
                />
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className={cn({
                [styles.hidden]: !displaySubmit && videoScroll && isMobile(),
              })}
            >
              <div className="mt-4 inline-block w-full">
                <div>
                  <p>Email*</p>
                </div>
                <input
                  ref={emailInput}
                  className={cn(styles.input, 'bodyText !mt-0')}
                  placeholder={
                    !!emailPlaceholderLabel ? emailPlaceholderLabel : ''
                  }
                  onChange={(e) => e.target.setCustomValidity('')}
                  required
                  onFocus={() => setDisplaySubmit(true)}
                  onClick={() => {
                    setDisplaySubmit(true)
                  }}
                />
              </div>

              <div
                className={cn(
                  styles.submitSection,
                  !displaySubmit && styles.hidden,
                )}
              >
                <div className={styles.checkboxes}>
                  {atLeastTwo(
                    enableConsumerSignup,
                    enableProfessionalSignup,
                    enablePartnerSignup,
                    enablePressSignup,
                  ) && (
                    <>
                      {enableConsumerSignup && (
                        <Checkbox
                          className="basis-1/2"
                          checked={checked.list === 'CONSUMERS'}
                          onChange={() =>
                            !(checked.list === 'CONSUMERS') &&
                            setChecked({
                              ...checked,
                              list: 'CONSUMERS',
                            })
                          }
                        >
                          <TrustedHtml
                            html={
                              !!consumerCheckboxLabel
                                ? consumerCheckboxLabel
                                : 'Consumer'
                            }
                          />
                        </Checkbox>
                      )}
                      {enableProfessionalSignup && (
                        <Checkbox
                          className="basis-1/2"
                          checked={checked.list === 'PROFESSIONALS'}
                          onChange={() =>
                            !(checked.list === 'PROFESSIONALS') &&
                            setChecked({
                              ...checked,
                              list: 'PROFESSIONALS',
                            })
                          }
                        >
                          <TrustedHtml
                            html={
                              !!professionalCheckboxLabel
                                ? professionalCheckboxLabel
                                : 'Professional'
                            }
                          />
                        </Checkbox>
                      )}
                      {enablePartnerSignup && (
                        <Checkbox
                          className="basis-1/2"
                          checked={checked.list === 'PARTNERS'}
                          onChange={() =>
                            !(checked.list === 'PARTNERS') &&
                            setChecked({
                              ...checked,
                              list: 'PARTNERS',
                            })
                          }
                        >
                          <TrustedHtml
                            html={
                              !!partnerCheckboxLabel
                                ? partnerCheckboxLabel
                                : 'Partner'
                            }
                          />
                        </Checkbox>
                      )}

                      {enablePressSignup && (
                        <Checkbox
                          className="basis-1/2"
                          checked={checked.list === 'PRESS'}
                          onChange={() =>
                            !(checked.list === 'PRESS') &&
                            setChecked({
                              ...checked,
                              list: 'PRESS',
                            })
                          }
                        >
                          <TrustedHtml
                            html={
                              !!pressCheckboxLabel
                                ? pressCheckboxLabel
                                : 'Press'
                            }
                          />
                        </Checkbox>
                      )}
                    </>
                  )}
                </div>
              </div>

              {fields && (
                <div className="mt-3 w-full mb-3">
                  {fields.map((field, index) => (
                    <GeneralInputField key={index} {...field} />
                  ))}
                </div>
              )}

              {signUpForNewsletter && (
                <div className="mt-5">
                  <Checkbox
                    className={styles.terms}
                    checked={checked.newsletterConsent}
                    onChange={() =>
                      setChecked({
                        ...checked,
                        newsletterConsent: !checked.newsletterConsent,
                      })
                    }
                  >
                    <TrustedHtml
                      className={styles.termsText}
                      html={newsletterConsentMessage}
                    />
                  </Checkbox>
                </div>
              )}

              <div>
                {enableAcceptTermsCheckbox ? (
                  <Checkbox
                    className={styles.terms}
                    checked={checked.terms}
                    onChange={() =>
                      setChecked({ ...checked, terms: !checked.terms })
                    }
                    required
                  >
                    <LineClamp
                      text={!!acceptTerms ? acceptTerms : 'Accept terms text'}
                      readMoreText={
                        !!acceptTermsReadMore ? acceptTermsReadMore : null
                      }
                      textClassname={styles.termsText}
                    />
                  </Checkbox>
                ) : (
                  <LineClamp
                    text={!!acceptTerms ? acceptTerms : 'Accept terms text'}
                    readMoreText={acceptTermsReadMore}
                    textClassname={styles.termsText}
                  />
                )}
              </div>

              <div className={styles.signupButton}>
                <Button type="submit" variant="primary">
                  {!!signupButtonLabel ? signupButtonLabel : 'Sign up'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </ClickOutside>
  ) : (
    <></>
  )
}

const GeneralInputField: FC<NewsletterField> = ({
  contentTypeAlias,
  ...args
}) => {
  switch (contentTypeAlias) {
    case 'newsletterCountrySelector':
      return (
        <CountrySelectorField {...(args as NewsletterCountrySelectorField)} />
      )
    case 'newsletterInputField':
      return <InputField {...(args as NewsletterInputField)} />
    case 'newsletterTextBlock':
      return (
        <TrustedHtml
          className="my-4"
          html={(args as NewsletterTextBlock).textBlock}
        />
      )
    case 'newsletterDropdownField':
      return <DropdownField {...(args as NewsletterDropdownField)} />
    default:
      return null
  }
}

const CountrySelectorField: FC<NewsletterCountrySelectorField> = ({
  ...args
}) => {
  // const countries = useSelector(selectCountries)
  const { data } = useCountries()
  return (
    <div className={'mt-4 w-full'}>
      <label htmlFor={args.sendgridMapping}>
        <TrustedHtml html={args.newsletterCountryLabel} />
      </label>

      <Select
        id={args.sendgridMapping}
        name={args.sendgridMapping}
        noMargin
        className="w-full"
      >
        {data?.countries
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((country, index) => (
            <option key={index} value={country.code}>
              {country.name}
            </option>
          ))}
      </Select>
    </div>
  )
}

const DropdownField: FC<NewsletterDropdownField> = ({ ...args }) => {
  return (
    <div className={'mt-4 w-full'}>
      <label htmlFor={args.sendgridMapping}>
        <TrustedHtml html={args.labelNewsletterDropdown} />
      </label>

      <Select
        id={args.sendgridMapping}
        name={args.sendgridMapping}
        noMargin
        className="w-full"
      >
        {args.dropdownItems.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </Select>
    </div>
  )
}

const InputField: FC<NewsletterInputField> = ({ ...args }) => {
  return (
    <div
      className={cn('mt-4', {
        [styles.inputHalf]: args.isHalf,
        'inline-block': args.isHalf,
        'w-full': !args.isHalf,
      })}
    >
      <label htmlFor={args.sendgridMapping}>
        <TrustedHtml html={args.newsletterInputLabel} />
      </label>

      {args.isTextArea ? (
        <textarea
          id={args.sendgridMapping}
          name={args.sendgridMapping}
          placeholder={args.placeholder}
          style={{ minHeight: '150px' }}
          className={cn(styles.input, styles.customInput, 'bodyText')}
        />
      ) : (
        <input
          id={args.sendgridMapping}
          name={args.sendgridMapping}
          placeholder={args.placeholder}
          required={args.isRequired}
          className={cn(styles.input, styles.customInput, 'bodyText')}
        />
      )}
    </div>
  )
}

export default Signup
